/**
 * goals.ts — the ordered objective list that powers BOTH the persistent goal
 * tracker and the first-run interactive tutorial. Each goal's completion is
 * evaluated with `meetsRequirement` (the Unlock system), so goals need no bespoke
 * logic. Goals with a `highlight` selector are tutorial steps: the spotlight
 * overlay points at that element until the goal is met.
 */
import type { GameState } from '../core/GameState';
import type { ExtendedRequirement } from './unlock';

export interface Goal {
  id: string;
  /** Short title for the tracker widget. */
  title: string;
  /** Instructional hint shown in the tutorial spotlight. */
  hint: string;
  requirement: ExtendedRequirement;
  /** CSS selector to spotlight during the tutorial (tutorial step if present). */
  highlight?: string;
  /** Tab to open automatically while this goal is active. */
  tab?: string;
}

export const GOALS: readonly Goal[] = [
  // ── Tutorial steps (have a highlight target) ──
  {
    id: 'mine', title: '25 Erz abbauen', hint: 'Tippe den leuchtenden ABBAU-Knopf, um Erz zu graben!',
    requirement: { totalOreMined: 25 }, highlight: '.mine-btn',
  },
  {
    id: 'sell', title: '400 $ verdienen', hint: 'Drücke „Alles verkaufen“, um dein Erz zu Geld zu machen.',
    requirement: { lifetimeEarnings: 400 }, highlight: '.sell-btn',
  },
  {
    id: 'drill', title: 'Auto-Bohrer kaufen', hint: 'Kauf einen Auto-Bohrer, damit die Mine für dich arbeitet.',
    requirement: { building: 'drill' }, highlight: '[data-buy="drill"]', tab: 'buildings',
  },
  {
    id: 'power', title: 'Kraftwerk kaufen', hint: 'Maschinen brauchen Energie — kauf ein Kraftwerk.',
    requirement: { building: 'powerplant' }, highlight: '[data-buy="powerplant"]', tab: 'buildings',
  },
  {
    id: 'grow', title: '1.000 $ verdienen', hint: 'Bau weiter ab und verkaufe, um zu wachsen.',
    requirement: { lifetimeEarnings: 1000 }, highlight: '.sell-btn',
  },
  {
    id: 'elevator', title: 'Aufzug bauen', hint: 'Bau einen Aufzugschacht, um tiefere Schichten zu erreichen.',
    requirement: { building: 'elevator' }, highlight: '[data-buy="elevator"]', tab: 'buildings',
  },
  {
    id: 'dig', title: 'Zu Kohle graben (Tiefe 1)', hint: 'Grab jetzt tiefer, um reicheres Erz freizuschalten!',
    requirement: { depth: 1 }, highlight: '.depth-btn',
  },
  // ── Ongoing objectives (tracker only) ──
  { id: 'tool', title: 'Werkzeug verbessern', hint: '', requirement: { toolLevel: 2 } },
  { id: 'iron', title: 'Eisen erreichen (Tiefe 3)', hint: '', requirement: { depth: 3 } },
  { id: 'research', title: 'Prospektion erforschen', hint: '', requirement: { tech: 'prospecting' } },
  { id: 'gold', title: 'Gold finden (Tiefe 5)', hint: '', requirement: { depth: 5 } },
  { id: 'prestige', title: 'Einmal Prestige machen', hint: '', requirement: { resets: 1 } },
  { id: 'core', title: 'Den Kern erreichen (Uran)', hint: '', requirement: { depth: 9 } },
] as const;

/** Index of the last tutorial step (the highest goal that has a highlight target). */
export const LAST_TUTORIAL_INDEX = GOALS.reduce((last, g, i) => (g.highlight ? i : last), 0);

/** Numeric progress toward a goal, inferred from its primary requirement clause. */
export function goalProgress(state: GameState, goal: Goal): { current: number; target: number } {
  const r = goal.requirement;
  if (r.totalOreMined !== undefined) return { current: state.stats.totalOreMined, target: r.totalOreMined };
  if (r.lifetimeEarnings !== undefined) return { current: state.stats.lifetimeEarnings, target: r.lifetimeEarnings };
  if (r.money !== undefined) return { current: state.economy.money, target: r.money };
  if (r.depth !== undefined) return { current: state.mining.maxDepth, target: r.depth };
  if (r.toolLevel !== undefined) return { current: state.mining.toolLevel, target: r.toolLevel };
  if (r.totalClicks !== undefined) return { current: state.stats.totalClicks, target: r.totalClicks };
  if (r.resets !== undefined) return { current: state.prestige.resets, target: r.resets };
  if (r.building !== undefined) {
    const id = Array.isArray(r.building) ? r.building[0] : r.building;
    return { current: state.buildings[id] ?? 0, target: Array.isArray(r.building) ? r.building[1] : 1 };
  }
  if (r.tech !== undefined) return { current: state.research.researched[r.tech] ? 1 : 0, target: 1 };
  return { current: 0, target: 1 };
}
